import Vue from 'vue'

// Track direction via Navigation API (fires before beforeEach for back/forward traversals)
let _isBack = false
if (typeof window !== 'undefined' && window.navigation) {
  window.navigation.addEventListener('navigate', (event) => {
    _isBack = event.navigationType === 'traverse' &&
      event.destination.index < window.navigation.currentEntry.index
  })
}

// Active transition context
let _ctx = null  // { transitionItemId, projectId, sourceEl, sourceProjEl, sourceGridEl, resolve }

function esc(id) { return CSS.escape(String(id)) }

/**
 * Called by router beforeEach BEFORE startViewTransition.
 * Imperatively sets view-transition-name on source elements so they're
 * captured in the old state snapshot.
 */
export function startTransition({ transitionItemId, projectId, resolve, isBack: isBackParam = null, thumbScale = null }) {
  // Prefer the explicitly-passed direction (derived from route names in the router).
  // Fall back to the Navigation API value only when the router didn't pass one.
  // The Navigation API doesn't fire for SPA pushState navigations, so _isBack can
  // get stuck as true after a back traversal if we rely on it alone.
  if (isBackParam !== null) _isBack = isBackParam
  cleanup()

  // Both screenshots crossfade. New scales in from source density; old scales out to dest density.
  // Forward (index→detail): new starts small (strip scale), old ends small.
  // Backward (detail→index): new starts large (detail scale), old ends large.
  if (thumbScale != null) {
    const fwd = thumbScale.toFixed(4)
    const bwd = (1 / thumbScale).toFixed(4)
    document.documentElement.style.setProperty('--vt-new-scale', _isBack ? bwd : fwd)
    document.documentElement.style.setProperty('--vt-old-scale', _isBack ? fwd : bwd)
  }

  const sourceEl = transitionItemId
    ? (document.querySelector(`[data-item-id="${esc(transitionItemId)}"] img`)
      || document.querySelector('.ds-page .frame-renderer__thumbnail'))
    : null
  if (sourceEl) sourceEl.style.viewTransitionName = 'ds-item-active'

  const sourceProjEl = projectId
    ? (document.querySelector(`[data-project-id="${esc(projectId)}"] .project-lane__name`)
      || document.querySelector(`[data-vt-project-header="${esc(projectId)}"] .project-title`))
    : null
  if (sourceProjEl) sourceProjEl.style.viewTransitionName = `project-header`

  // Treat the entire grid as one unit: assign a single VT name to the .ds-grid element.
  let sourceGridEl = null
  if (projectId && !transitionItemId) {
    const p = `[data-project-id="${esc(projectId)}"]`
    sourceGridEl = document.querySelector(`${p}.ds-grid`) || document.querySelector(`${p} .ds-grid`)
    if (sourceGridEl) sourceGridEl.style.viewTransitionName = 'ds-thumb-area'
  }

  _ctx = { transitionItemId, projectId, sourceEl, sourceProjEl, sourceGridEl, resolve }
}

/**
 * Called by the destination component when its transition element is rendered.
 * Pass destEl explicitly to avoid keep-alive DOM collisions (scoped to component $el).
 */
export function signalReady(destEl = null) {
  if (!_ctx?.resolve) return
  const { transitionItemId, projectId, sourceEl, sourceProjEl, sourceGridEl, resolve } = _ctx

  // Clear source names — old state was already captured by startViewTransition
  if (sourceEl) sourceEl.style.viewTransitionName = ''
  if (sourceProjEl) sourceProjEl.style.viewTransitionName = ''
  if (sourceGridEl) sourceGridEl.style.viewTransitionName = ''

  // Set destination item name (use passed element, or fall back to document query)
  const itemTarget = destEl || (transitionItemId
    ? (() => {
        const all = [...document.querySelectorAll(`[data-item-id="${esc(transitionItemId)}"] img`)]
        return all.find(el => el !== sourceEl) || null
      })()
    : null)
  if (itemTarget) itemTarget.style.viewTransitionName = 'ds-item-active'

  // Set destination project header name
  if (projectId) {
    const all = [...document.querySelectorAll(
      `[data-project-id="${esc(projectId)}"] .project-lane__name, [data-vt-project-header="${esc(projectId)}"] .project-title`
    )]
    const projTarget = all.find(el => el !== sourceProjEl) || null
    if (projTarget) projTarget.style.viewTransitionName = `project-header`
  }

  // Set destination grid name (exclude source element to handle keep-alive)
  if (sourceGridEl && projectId) {
    const p = `[data-project-id="${esc(projectId)}"]`
    const all = [...document.querySelectorAll(`${p}.ds-grid, ${p} .ds-grid`)]
    const destGrid = all.find(el => el !== sourceGridEl)
    if (destGrid) destGrid.style.viewTransitionName = 'ds-thumb-area'
  }

  const fn = resolve
  _ctx.resolve = null  // prevent double-call

  // The grid is a div screenshot — no image-load wait needed
  Vue.nextTick(fn)
}

/**
 * Called by router transition.finished to clear all imperative VT names.
 */
export function cleanup() {
  if (!_ctx) return
  const { sourceEl, sourceProjEl } = _ctx
  if (sourceEl) sourceEl.style.viewTransitionName = ''
  if (sourceProjEl) sourceProjEl.style.viewTransitionName = ''
  // Sweep any remaining imperative VT names
  document.querySelectorAll('[style*="view-transition-name"]').forEach(el => {
    const vtn = el.style.viewTransitionName
    if (vtn && vtn !== 'none') el.style.viewTransitionName = ''
  })
  document.documentElement.style.removeProperty('--vt-old-scale')
  document.documentElement.style.removeProperty('--vt-new-scale')
  _ctx = null
}

export function getContext() { return _ctx }
export function isBack() { return _isBack }
